/**
 * /dashboard — full user dashboard.
 *
 * Founder direction 2026-05-24: two-menu split.
 *   - TOP (financial identity): avatar + name + region, big balance
 *     with today's delta, Deposit/Withdraw inline, 4 stat tiles
 *     (Portfolio, Orders, Rewards, Settings) doubling as deep links.
 *   - BOTTOM (social / community): My Copytraders + Creators I Follow
 *     2-col split, then Leaderboard rail, then Activity feed.
 *
 * Industry reference: Robinhood top section ("money first") + eToro
 * bottom section ("people"). Coinbase contributed the 4-action row,
 * Stake contributed the leaderboard rail.
 *
 * v1 dashboard at /home is intentionally left untouched (founder
 * picked "New route /dashboard, leave /home alone" in the design Q&A).
 */
"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  ArrowUpRight,
  ArrowDownRight,
  Briefcase,
  ClipboardList,
  Gift,
  Settings as SettingsIcon,
  Users,
  Star,
  Crown,
  TrendingUp,
  type LucideIcon,
} from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteSidebar } from "@/components/SiteSidebar";
import { useRouter } from "next/navigation";
import { api, clearTokens, loadTokens } from "@/lib/api";

interface StatTile {
  label: string;
  value: string;
  hint: string;
  icon: LucideIcon;
  href: string;
}

interface FollowedTrader {
  username: string;
  monthlyReturn: number;
  badge?: string;
}

interface FollowedCreator {
  username: string;
  followers: number;
}

interface LeaderboardRow {
  rank: number;
  username: string;
  pnlCents: number;
  badge: string;
}

interface ActivityRow {
  id: string;
  username: string;
  verb: string;
  target: string;
  occurredMinutesAgo: number;
}

// Mock data — copytraders / creators / leaderboard / activity tables
// aren't wired to the API yet. Structure mirrors the eventual shape so
// the wire-up is a 1:1 swap.
const MOCK_TRADERS: FollowedTrader[] = [
  { username: "whale_alpha", monthlyReturn: 18.4, badge: "Diamond" },
  { username: "sports_iq", monthlyReturn: 9.1, badge: "Gold" },
  { username: "politico", monthlyReturn: -2.3 },
];

const MOCK_CREATORS: FollowedCreator[] = [
  { username: "marketwatch", followers: 12400 },
  { username: "crypto_skeptic", followers: 8200 },
  { username: "lula_2026", followers: 5400 },
];

const MOCK_LEADERBOARD: LeaderboardRow[] = [
  { rank: 1, username: "whale_alpha", pnlCents: 4218000, badge: "Diamond" },
  { rank: 2, username: "tia_brazil", pnlCents: 2950000, badge: "Diamond" },
  { rank: 3, username: "sports_iq", pnlCents: 1810000, badge: "Gold" },
  { rank: 4, username: "marketwatch", pnlCents: 1240000, badge: "Gold" },
  { rank: 5, username: "crypto_skeptic", pnlCents: 880000, badge: "Silver" },
];

const MOCK_ACTIVITY: ActivityRow[] = [
  {
    id: "a1",
    username: "whale_alpha",
    verb: "bought YES on",
    target: "Brazil wins 2026 FIFA World Cup",
    occurredMinutesAgo: 4,
  },
  {
    id: "a2",
    username: "sports_iq",
    verb: "added a position on",
    target: "Will Bitcoin close 2026 above $200K?",
    occurredMinutesAgo: 28,
  },
  {
    id: "a3",
    username: "marketwatch",
    verb: "published a market analysis on",
    target: "Will Lula win 2026 election?",
    occurredMinutesAgo: 95,
  },
];

export default function DashboardPage() {
  const router = useRouter();
  const [username, setUsername] = useState<string | null>(null);
  const [balanceUsd, setBalanceUsd] = useState<number | null>(null);

  function handleSignOut() {
    clearTokens();
    setUsername(null);
    router.push("/");
  }

  useEffect(() => {
    const tokens = loadTokens();
    if (!tokens) {
      setUsername(null);
      return;
    }
    setUsername(tokens.user.username);
    (async () => {
      try {
        const wallets = await api.listMyWallets();
        const usdc =
          wallets.find((w) => w.currency === "USDC") ?? wallets[0] ?? null;
        if (usdc) setBalanceUsd(Number(usdc.balance_cents) / 100);
      } catch {
        /* swallow */
      }
    })();
  }, []);

  const initial = username ? username[0]?.toUpperCase() : "?";
  const balanceDisplay = balanceUsd != null ? `$${balanceUsd.toFixed(2)}` : "—";
  // Mock today-delta — wire to /v1/me/equity-curve when available.
  const todayDeltaUsd = 23.4;
  const todayDeltaPct = 3.4;
  const deltaPositive = todayDeltaUsd >= 0;

  const stats: StatTile[] = [
    {
      // 2026-05-24 — renamed Portfolio → Transaction history per
      // founder direction. Stat value still counts open positions
      // (most actionable number for the tile); the page itself
      // covers full transaction history when implemented.
      label: "Transaction history",
      value: "12",
      hint: "open positions",
      icon: Briefcase,
      href: "/home?tab=positions",
    },
    {
      label: "Orders",
      value: "3",
      hint: "open",
      icon: ClipboardList,
      href: "/home?tab=orders",
    },
    {
      label: "Rewards",
      value: "Diamond",
      hint: "$50 monthly bonus",
      icon: Gift,
      href: "/rewards",
    },
    {
      label: "Settings",
      value: "KYC ✓",
      hint: "Tier 1 verified",
      icon: SettingsIcon,
      href: "/settings",
    },
  ];

  return (
    <>
      <SiteHeader />
      <div className="mx-auto grid w-full max-w-[1400px] grid-cols-1 gap-5 px-5 py-4 md:grid-cols-[200px_1fr]">
        <SiteSidebar />
        <main className="flex min-w-0 flex-col gap-6">
          {/* ═══════ TOP — FINANCIAL IDENTITY ═══════ */}
          <section
            className="rounded-2xl border p-5 sm:p-6"
            style={{
              background: "var(--color-surface)",
              borderColor: "var(--color-border)",
            }}
          >
            {/* Identity row */}
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <span
                  aria-hidden="true"
                  className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-full text-[24px] font-bold"
                  style={{
                    background: "var(--color-surface2)",
                    border: "1px solid var(--color-border)",
                    color: "var(--color-text)",
                  }}
                >
                  {initial}
                </span>
                <div className="min-w-0">
                  <div className="text-[18px] font-bold tracking-tight">
                    {username ?? "Sign in to view your dashboard"}
                  </div>
                  {username && (
                    <div
                      className="flex flex-wrap items-center gap-x-2 text-[12px]"
                      style={{ color: "var(--color-text3)" }}
                    >
                      <span>@{username} · 🇺🇸 US · KYC Tier 1</span>
                      <span aria-hidden="true">·</span>
                      {/* 2026-05-24 — sign-out moved here from the
                          top-nav Power-icon button. Text link, not a
                          prominent button, per founder direction. */}
                      <button
                        type="button"
                        onClick={handleSignOut}
                        className="underline-offset-2 transition-colors hover:underline"
                        style={{ color: "var(--color-text2)" }}
                      >
                        Sign out
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Deposit / Withdraw inline */}
              <div className="flex flex-wrap gap-2">
                <Link
                  href="/deposit"
                  className="inline-flex h-10 items-center gap-2 rounded-lg px-4 text-[13px] font-bold"
                  style={{
                    background: "var(--color-accent)",
                    color: "#0a0a0a",
                  }}
                >
                  <ArrowDownToLine size={15} strokeWidth={2} />
                  Deposit
                </Link>
                <Link
                  href="/withdraw"
                  className="inline-flex h-10 items-center gap-2 rounded-lg border px-4 text-[13px] font-semibold"
                  style={{
                    borderColor: "var(--color-border)",
                    color: "var(--color-text)",
                  }}
                >
                  <ArrowUpFromLine size={15} strokeWidth={2} />
                  Withdraw
                </Link>
              </div>
            </div>

            {/* Big balance + today's delta */}
            <div className="mt-6 flex flex-wrap items-baseline gap-x-4 gap-y-1">
              <span
                className="font-mono text-[40px] font-bold tabular-nums tracking-[-0.025em] sm:text-[52px]"
                style={{ color: "var(--color-text)" }}
              >
                {balanceDisplay}
              </span>
              <span
                className="inline-flex items-center gap-1 font-mono text-[14px] font-semibold tabular-nums"
                style={{
                  color: deltaPositive
                    ? "var(--color-yes)"
                    : "var(--color-no)",
                }}
              >
                {deltaPositive ? (
                  <ArrowUpRight size={14} strokeWidth={2.25} />
                ) : (
                  <ArrowDownRight size={14} strokeWidth={2.25} />
                )}
                {deltaPositive ? "+" : ""}${todayDeltaUsd.toFixed(2)} · {todayDeltaPct.toFixed(1)}% today
              </span>
            </div>
            <div
              className="mt-1 font-mono text-[10px] uppercase tracking-[0.12em]"
              style={{ color: "var(--color-text3)" }}
            >
              Virtual balance · USDC settlement
            </div>

            {/* Stat tiles */}
            <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {stats.map((s) => (
                <StatTileCard key={s.label} tile={s} />
              ))}
            </div>
          </section>

          {/* ═══════ BOTTOM — SOCIAL / COMMUNITY ═══════ */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <SectionCard
              icon={Users}
              title="My copytraders"
              count={MOCK_TRADERS.length}
              actionLabel="Discover top traders →"
              actionHref="/leaderboard"
            >
              <ul className="space-y-2.5">
                {MOCK_TRADERS.map((t) => (
                  <li
                    key={t.username}
                    className="flex items-center gap-3"
                  >
                    <span
                      aria-hidden="true"
                      className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-[12px] font-bold"
                      style={{
                        background: "var(--color-surface2)",
                        border: "1px solid var(--color-border)",
                        color: "var(--color-text)",
                      }}
                    >
                      {t.username[0]?.toUpperCase()}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[13px] font-semibold">
                        @{t.username}
                      </div>
                      {t.badge && (
                        <div
                          className="font-mono text-[10px] uppercase tracking-[0.1em]"
                          style={{ color: "var(--color-accent)" }}
                        >
                          {t.badge}
                        </div>
                      )}
                    </div>
                    <span
                      className="font-mono text-[12px] font-bold tabular-nums"
                      style={{
                        color:
                          t.monthlyReturn >= 0
                            ? "var(--color-yes)"
                            : "var(--color-no)",
                      }}
                    >
                      {t.monthlyReturn >= 0 ? "+" : ""}
                      {t.monthlyReturn.toFixed(1)}%
                    </span>
                  </li>
                ))}
              </ul>
            </SectionCard>

            <SectionCard
              icon={Star}
              title="Creators I follow"
              count={MOCK_CREATORS.length}
              actionLabel="Find creators →"
              actionHref="/creators"
            >
              <ul className="space-y-2.5">
                {MOCK_CREATORS.map((c) => (
                  <li
                    key={c.username}
                    className="flex items-center gap-3"
                  >
                    <span
                      aria-hidden="true"
                      className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-[12px] font-bold"
                      style={{
                        background: "var(--color-surface2)",
                        border: "1px solid var(--color-border)",
                        color: "var(--color-text)",
                      }}
                    >
                      {c.username[0]?.toUpperCase()}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[13px] font-semibold">
                        @{c.username}
                      </div>
                      <div
                        className="text-[11px]"
                        style={{ color: "var(--color-text3)" }}
                      >
                        {formatFollowers(c.followers)} followers
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </SectionCard>
          </div>

          {/* Leaderboard */}
          <SectionCard
            icon={Crown}
            title="Leaderboard"
            subtitle="Top traders this month"
            actionLabel="Full leaderboard →"
            actionHref="/leaderboard"
          >
            <ul className="space-y-2">
              {MOCK_LEADERBOARD.map((row) => (
                <li
                  key={row.rank}
                  className="flex items-center gap-3 rounded-lg px-2 py-2"
                  style={{
                    background:
                      row.rank <= 3 ? "var(--color-bg2)" : "transparent",
                  }}
                >
                  <span
                    className="w-7 flex-shrink-0 font-mono text-[14px] font-bold tabular-nums"
                    style={{
                      color:
                        row.rank === 1
                          ? "var(--color-accent)"
                          : "var(--color-text2)",
                    }}
                  >
                    #{row.rank}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-[13px] font-semibold">
                    @{row.username}
                  </span>
                  <span
                    className="hidden font-mono text-[10px] uppercase tracking-[0.1em] sm:inline"
                    style={{ color: "var(--color-accent)" }}
                  >
                    {row.badge}
                  </span>
                  <span
                    className="font-mono text-[13px] font-bold tabular-nums"
                    style={{ color: "var(--color-yes)" }}
                  >
                    +${(row.pnlCents / 100).toLocaleString()}
                  </span>
                </li>
              ))}
            </ul>
          </SectionCard>

          {/* Activity feed */}
          <SectionCard
            icon={TrendingUp}
            title="Activity from people you follow"
            actionLabel="View full feed →"
            actionHref="/feed"
          >
            <ul className="space-y-3">
              {MOCK_ACTIVITY.map((row) => (
                <li
                  key={row.id}
                  className="flex items-start gap-3 border-b pb-3 last:border-b-0 last:pb-0"
                  style={{ borderColor: "var(--color-border)" }}
                >
                  <span
                    aria-hidden="true"
                    className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-[12px] font-bold"
                    style={{
                      background: "var(--color-surface2)",
                      border: "1px solid var(--color-border)",
                      color: "var(--color-text)",
                    }}
                  >
                    {row.username[0]?.toUpperCase()}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px]">
                      <span className="font-semibold">@{row.username}</span>{" "}
                      <span style={{ color: "var(--color-text2)" }}>
                        {row.verb}
                      </span>{" "}
                      <span className="font-medium">{row.target}</span>
                    </div>
                    <div
                      className="mt-0.5 text-[11px]"
                      style={{ color: "var(--color-text3)" }}
                    >
                      {formatMinutesAgo(row.occurredMinutesAgo)}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </SectionCard>

          {/* Bottom navigation row — links to the three account
              sub-pages under /dashboard/*. Founder direction
              2026-05-24: easy paths to transaction history, creator
              subscriptions, and copytraders. Subpage Shell handles
              the "back to dashboard" return. */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <BottomNavCard
              href="/dashboard/transactions"
              title="Transaction history"
              body="Trades, deposits, withdrawals, bonuses, tips, subscriptions — every money + share movement."
            />
            <BottomNavCard
              href="/dashboard/creators"
              title="Manage subscriptions"
              body="Creators you follow + paid tiers. Change plans, unsubscribe, or upgrade in one place."
            />
            <BottomNavCard
              href="/dashboard/copytraders"
              title="Copytraders"
              body="Traders you mirror with cumulative P/L and a separate transactions feed for copy-trade activity."
            />
          </div>
        </main>
      </div>
    </>
  );
}

function BottomNavCard({
  href,
  title,
  body,
}: {
  href: string;
  title: string;
  body: string;
}) {
  return (
    <Link
      href={href}
      className="flex flex-col gap-2 rounded-2xl border p-5 transition-colors no-underline"
      style={{
        background: "var(--color-surface)",
        borderColor: "var(--color-border)",
        color: "var(--color-text)",
      }}
    >
      <div className="flex items-center justify-between">
        <h3 className="text-[15px] font-bold tracking-tight">{title}</h3>
        <span
          aria-hidden="true"
          className="text-[16px] font-semibold"
          style={{ color: "var(--color-accent)" }}
        >
          →
        </span>
      </div>
      <p
        className="text-[12px] leading-relaxed"
        style={{ color: "var(--color-text2)" }}
      >
        {body}
      </p>
    </Link>
  );
}

function StatTileCard({ tile }: { tile: StatTile }) {
  const Icon = tile.icon;
  return (
    <Link
      href={tile.href}
      className="flex flex-col gap-2 rounded-xl border p-3.5 no-underline transition-colors"
      style={{
        background: "var(--color-bg)",
        borderColor: "var(--color-border)",
        color: "var(--color-text)",
      }}
    >
      <div className="flex items-center justify-between">
        <Icon
          size={14}
          strokeWidth={1.75}
          aria-hidden="true"
          style={{ color: "var(--color-text2)" }}
        />
        <span
          className="font-mono text-[10px] uppercase tracking-[0.12em]"
          style={{ color: "var(--color-text3)" }}
        >
          {tile.label}
        </span>
      </div>
      <div className="text-[18px] font-bold">{tile.value}</div>
      <div
        className="text-[11px]"
        style={{ color: "var(--color-text3)" }}
      >
        {tile.hint}
      </div>
    </Link>
  );
}

function SectionCard({
  icon: Icon,
  title,
  subtitle,
  count,
  actionLabel,
  actionHref,
  children,
}: {
  icon: LucideIcon;
  title: string;
  subtitle?: string;
  count?: number;
  actionLabel: string;
  actionHref: string;
  children: React.ReactNode;
}) {
  return (
    <section
      className="rounded-2xl border p-5"
      style={{
        background: "var(--color-surface)",
        borderColor: "var(--color-border)",
      }}
    >
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Icon
            size={16}
            strokeWidth={1.75}
            aria-hidden="true"
            style={{ color: "var(--color-accent)" }}
          />
          <h2 className="text-[15px] font-bold tracking-tight">
            {title}
            {count != null && (
              <span
                className="ml-1.5 font-mono text-[11px] font-normal"
                style={{ color: "var(--color-text3)" }}
              >
                {count}
              </span>
            )}
          </h2>
        </div>
        <Link
          href={actionHref}
          className="flex-shrink-0 text-[12px] font-medium"
          style={{ color: "var(--color-accent)" }}
        >
          {actionLabel}
        </Link>
      </div>
      {subtitle && (
        <p
          className="-mt-2 mb-4 text-[12px]"
          style={{ color: "var(--color-text2)" }}
        >
          {subtitle}
        </p>
      )}
      {children}
    </section>
  );
}

function formatFollowers(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}

function formatMinutesAgo(mins: number): string {
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}
